import { Component, Inject, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Store } from '@ngxs/store';
import { Observable, Subject, takeUntil } from 'rxjs';
import { PricelistState } from 'src/app/core/states/pricelist/pricelist.state';
import {
  CreatePricelist,
  GetPricelists,
} from '../../core/states/pricelist/pricelist.actions';
import { PriceListApi, PriceListDTO, RoleDTO } from '@lkp/parking-sdk';
import { PRICE_LIST_API } from '../../core/providers/api.module';
import { Router } from '@angular/router';
import { CreatePricelistComponent } from 'src/app/components/create-pricelist/create-pricelist.component';
import { CopyPricelistModalComponent } from 'src/app/components/copy-pricelist-modal/copy-pricelist-modal.component';
import { UsersState } from 'src/app/core/states/users/users.state';
import { DateService } from 'src/app/core/services/date.service';

@Component({
    selector: 'app-pricelists',
    templateUrl: './pricelists.component.html',
    styleUrls: ['./pricelists.component.scss'],
    standalone: false
})
export class PricelistsComponent implements OnInit {
  dataSource$: Observable<PriceListDTO[]>;
  displayedColumns: string[] = ['name', 'validFrom', 'validTo'];
  isAdmin: boolean = false;
  unpricedItems = false;
  missingFeaturePrices?: string[];

  readonly onDestroy$ = new Subject<void>();

  constructor(
    public dialog: MatDialog,
    readonly store: Store,
    readonly router: Router,
    @Inject(PRICE_LIST_API) readonly priceListApi: PriceListApi,
    readonly dateService: DateService
  ) {
    this.dataSource$ = this.store.select(PricelistState.currentPriceLists);
  }

  ngOnInit() {
    this.store.dispatch(new GetPricelists());
    this.store
      .selectOnce(UsersState.loggedInUser)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((user) => {
        this.isAdmin = user?.roles
          ? user.roles.some((role: RoleDTO) => role.name === 'ROLE_ADMIN')
          : false;
      });
    this.getFeaturePrices();
  }

  getFeaturePrices() {
    this.priceListApi
      .getMissingPricesAdmin()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((missingPrices) => {
        this.missingFeaturePrices = [];
        this.unpricedItems = !!missingPrices.features?.length;

        missingPrices.features?.forEach((feature) => {
          this.missingFeaturePrices?.push(
            feature.featureName! +
              ', ' +
              'sista datum: ' +
              this.formatDate(feature.lastDate)
          );
        });
      });
  }

  navigateToPriceListById(priceListId: number): void {
    this.router.navigate(['/prislista', priceListId]);
  }

  openCopyPricelistModal() {
    const dialogRef = this.dialog.open(CopyPricelistModalComponent, {
      width: '500px',
      data: {},
    });

    dialogRef
      .afterClosed()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((result: PriceListDTO) => {
        if (result) {
          const copiedPriceList: PriceListDTO = result;
          this.store.dispatch(new CreatePricelist(copiedPriceList));
        }
      });
  }

  openCreatePricelistModal() {
    const dialogRef = this.dialog.open(CreatePricelistComponent, {
      data: {},
    });

    dialogRef
      .afterClosed()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((result) => {
        if (result) {
          const newPriceList: PriceListDTO = {
            name: result.name,
            validFrom: result.validFrom,
            validTo: result.validTo,
          };
          this.store
            .dispatch(new CreatePricelist(newPriceList))
            .pipe(takeUntil(this.onDestroy$))
            .subscribe(() => {
              this.store
                .selectOnce(PricelistState.selectedPriceLists)
                .pipe(takeUntil(this.onDestroy$))
                .subscribe((pricelist) => {
                  this.router.navigate(['prislista', pricelist?.priceListId]);
                });
            });
        }
      });
  }

  formatDate(date?: Date | string) {
    return (date && this.dateService.formatDate(date)) || '';
  }

  ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }
}
